import { useState } from "react";
import { Star, ImageOff, Wand2 } from "lucide-react";
import type { SpyAd } from "../types/adsSpy.types";
import { scoreColor, parseAiAnalysis } from "../utils/helpers";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface SpyAdCardProps {
  ad: SpyAd;
  onClickDetail: (id: number) => void;
  onToggleBookmark: (id: number) => void;
  onCreateSimilar?: (ad: SpyAd) => void;
  onMediaError?: (adId: number) => void;
}

export default function SpyAdCard({
  ad,
  onClickDetail,
  onToggleBookmark,
  onCreateSimilar,
  onMediaError,
}: SpyAdCardProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;
  const mediaUrl = ad.thumbnail_url || ad.image_url || "";
  const analysis = parseAiAnalysis(ad.ai_analysis as string | Record<string, unknown> | null);
  const score = analysis?.score != null ? Number(analysis.score) : null;
  const [imgError, setImgError] = useState(false);

  const showFallback = !mediaUrl || imgError;

  return (
    <div
      className="group bg-white border border-gray-200 rounded-[20px] overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-all duration-200"
      onClick={() => onClickDetail(ad.id)}
    >
      {/* Media */}
      <div className="relative overflow-hidden bg-gray-100 aspect-square">
        {!showFallback ? (
          <img
            src={mediaUrl}
            alt={ad.headline || "Ad"}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => {
              setImgError(true);
              onMediaError?.(ad.id);
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-1.5">
            <ImageOff className="w-5 h-5 text-gray-300" />
            <span className="text-[10px] text-gray-400">
              {!mediaUrl ? t("no-media", "No media") : t("ad-expired", "Ad expired")}
            </span>
          </div>
        )}

        {/* Media type badge */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/70 backdrop-blur-sm text-gray-600">
            {ad.media_type === "Video" ? "Video" : t("image", "Image")}
          </span>
          {analysis && (
            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-white/70 backdrop-blur-sm">
              &#10024;
            </span>
          )}
        </div>

        {/* Bookmark button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleBookmark(ad.id);
          }}
          className={`absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
            ad.is_bookmarked
              ? ""
              : "opacity-0 group-hover:opacity-100"
          }`}
          style={{
            background: ad.is_bookmarked
              ? "rgba(245,197,24,0.2)"
              : "rgba(255,255,255,0.6)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Star
            className="w-3.5 h-3.5"
            fill={ad.is_bookmarked ? "#E08700" : "none"}
            color={ad.is_bookmarked ? "#E08700" : "#9CA3AF"}
          />
        </button>

        {/* Impression rank */}
        {ad.impression_rank > 0 && (
          <div className="absolute bottom-2.5 left-2.5 inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white">
            #{ad.impression_rank}
          </div>
        )}

        {/* AI score */}
        {score !== null && (
          <div
            className="absolute bottom-2.5 right-2.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full backdrop-blur-sm"
            style={{
              color: scoreColor(score),
              background: "rgba(255,255,255,0.8)",
            }}
          >
            {score}/10
          </div>
        )}

        {/* Create similar button — hover overlay */}
        {onCreateSimilar && !showFallback && ad.media_type === "Image" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCreateSimilar(ad);
            }}
            className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-semibold text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-200 hover:brightness-110"
            style={{ background: "linear-gradient(to right, rgba(67,104,229,0.85), rgba(77,181,255,0.85))" }}
          >
            <Wand2 className="w-3 h-3" />
            {t("create-similar", "Create similar")}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-4">
        <div className="text-xs font-medium text-gray-400 mb-1">
          {ad.brand_name}
        </div>
        <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug">
          {ad.headline || ad.ad_copy?.substring(0, 80) || t("no-text", "No text")}
        </p>
      </div>
    </div>
  );
}
